'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { ArrowRight, MessageSquare, TrendingDown, TrendingUp } from 'lucide-react';
import type { TradeCardData } from '@/app/home-feed/components/trade-types';
import { marketColors, formatPrice, getRR } from '@/app/home-feed/components/trade-types';
import { localizedTitle, type ForumThread } from '@/app/forum/lib';
import { timeAgoShort } from '@/lib/dates';
import { useLang } from '@/lib/i18n/LanguageContext';

// "Najnovije na ShowMeTrade" — the landing page's proof of life (owner request, 2026-09-02):
// the newest SIGNAL, the newest ANALYSIS and the newest forum topic, one card each, pulled
// from the same public APIs the feed and forum use. Any card whose source is empty is simply
// left out; the whole strip disappears if nothing is live yet.
export default function LatestStrip() {
  const { t, lang } = useLang();
  const [signal, setSignal] = useState<TradeCardData | null>(null);
  const [analysis, setAnalysis] = useState<TradeCardData | null>(null);
  const [thread, setThread] = useState<ForumThread | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const grab = async (url: string) => {
      try {
        const res = await fetch(url);
        if (!res.ok) return null;
        return await res.json();
      } catch {
        return null;
      }
    };
    (async () => {
      const [s, a, f] = await Promise.all([
        grab('/api/trades?tab=all&sort=newest&ideaType=SIGNAL&limit=1&offset=0'),
        grab('/api/trades?tab=all&sort=newest&ideaType=ANALYSIS&limit=1&offset=0'),
        grab('/api/forum/threads?category=all&sort=new&limit=1&offset=0'),
      ]);
      if (cancelled) return;
      setSignal(s?.trades?.[0] ?? null);
      setAnalysis(a?.trades?.[0] ?? null);
      setThread(f?.threads?.[0] ?? null);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  if (!loading && !signal && !analysis && !thread) return null;

  const label = (text: string) => (
    <div className="text-[11px] font-extrabold uppercase" style={{ color: 'var(--text-muted)', letterSpacing: '1.2px' }}>{text}</div>
  );
  const cardStyle = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 18 } as const;
  const skeleton = <div className="animate-pulse rounded-[18px]" style={{ background: 'var(--bg-sunken)', minHeight: 210 }} />;

  return (
    <section className="px-4 sm:px-8 pb-14 max-w-screen-2xl mx-auto">
      <div className="flex items-end justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h2 className="font-heading font-extrabold text-[24px]" style={{ color: 'var(--text-primary)', letterSpacing: '-0.4px' }}>
            {t('Latest on ShowMeTrade')}
          </h2>
          <p className="text-[14px] mt-0.5" style={{ color: 'var(--text-secondary)' }}>
            {t('Fresh from the community — right now, not last month.')}
          </p>
        </div>
        <Link href="/home-feed" className="inline-flex items-center gap-1 text-[14px] font-semibold" style={{ color: 'var(--accent-bright)' }}>
          {t('See the feed')} <ArrowRight size={14} />
        </Link>
      </div>

      <div className="grid md:grid-cols-3 gap-5">
        {/* ── Newest signal ── */}
        {loading ? skeleton : signal && (() => {
          const isBuy = signal.direction === 'BUY';
          const mc = marketColors[signal.market] ?? { bg: 'var(--info-muted)', text: 'var(--info)' };
          const rr = getRR(signal.entryPrice, signal.stopLoss, signal.takeProfit1, signal.direction);
          const live = signal.status === 'OPEN' || signal.status === 'PENDING';
          const statusColor = signal.status === 'WIN' ? 'var(--bullish)' : signal.status === 'LOSS' ? 'var(--bearish)' : 'var(--info)';
          const statusLabel = signal.status === 'WIN' ? t('HIT TP') : signal.status === 'LOSS' ? t('HIT SL') : live ? t('OPEN') : signal.status;
          return (
            <Link href={`/ideas/${signal.id}`} className="flex flex-col gap-3 p-5 transition-transform duration-150 hover:-translate-y-0.5" style={cardStyle}>
              <div className="flex items-center justify-between gap-2">
                {label(t('Newest signal'))}
                <span className="rounded-full text-[10px] font-extrabold px-2.5 py-1" style={{ border: `1.5px solid ${statusColor}`, color: statusColor, letterSpacing: '0.4px' }}>
                  {live ? '● ' : ''}{statusLabel}
                </span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono-nums font-bold text-[19px]" style={{ color: 'var(--text-primary)' }}>{signal.ticker}</span>
                <span className={`${isBuy ? 'badge-long' : 'badge-short'} inline-flex items-center gap-1 text-[11px] font-extrabold rounded-lg px-2.5 py-1`}>
                  {isBuy ? <TrendingUp size={11} /> : <TrendingDown size={11} />}{isBuy ? 'LONG' : 'SHORT'}
                </span>
                <span className="text-[11px] font-extrabold rounded-lg px-2.5 py-1" style={{ background: mc.bg, color: mc.text }}>{signal.market}</span>
              </div>
              <div className="flex gap-2">
                {[
                  { k: 'ENTRY', v: formatPrice(signal.entryPrice), c: 'var(--win)' },
                  { k: 'SL', v: formatPrice(signal.stopLoss), c: 'var(--bearish)' },
                  { k: 'TP', v: formatPrice(signal.takeProfit1), c: 'var(--bullish)' },
                ].map((c) => (
                  <div key={c.k} className="flex-1 rounded-lg p-2 text-center" style={{ background: 'var(--bg-base)' }}>
                    <div className="text-[10px] font-extrabold" style={{ color: 'var(--text-muted)', letterSpacing: '0.5px' }}>{c.k}</div>
                    <div className="font-mono-nums font-bold text-[13px]" style={{ color: c.c }}>{c.v}</div>
                  </div>
                ))}
                {rr !== '—' && (
                  <div className="flex-1 rounded-lg p-2 text-center" style={{ background: 'var(--accent-muted)' }}>
                    <div className="text-[10px] font-extrabold" style={{ color: 'var(--accent)', letterSpacing: '0.5px' }}>R:R</div>
                    <div className="font-mono-nums font-bold text-[13px]" style={{ color: 'var(--accent)' }}>1:{rr}</div>
                  </div>
                )}
              </div>
              <AuthorRow trade={signal} />
            </Link>
          );
        })()}

        {/* ── Newest analysis ── */}
        {loading ? skeleton : analysis && (() => {
          const mc = marketColors[analysis.market] ?? { bg: 'var(--info-muted)', text: 'var(--info)' };
          const img = analysis.imageUrl || analysis.chartImage || analysis.thumbnailUrl;
          return (
            <Link href={`/ideas/${analysis.id}`} className="flex flex-col gap-3 p-5 transition-transform duration-150 hover:-translate-y-0.5" style={cardStyle}>
              {label(t('Newest analysis'))}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono-nums font-bold text-[19px]" style={{ color: 'var(--text-primary)' }}>{analysis.ticker}</span>
                <span className="text-[11px] font-extrabold rounded-lg px-2.5 py-1" style={{ background: mc.bg, color: mc.text }}>{analysis.market}</span>
              </div>
              {img ? (
                <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border-subtle)', background: 'var(--bg-sunken)', aspectRatio: '16 / 8' }}>
                  <img src={img} alt={analysis.ticker} className="w-full h-full object-cover" loading="lazy" />
                </div>
              ) : null}
              {analysis.caption && (
                <p className="text-[14px] leading-[1.55]" style={{ color: 'var(--text-body)', display: '-webkit-box', WebkitLineClamp: img ? 2 : 4, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {analysis.caption}
                </p>
              )}
              <AuthorRow trade={analysis} />
            </Link>
          );
        })()}

        {/* ── Newest forum topic ── */}
        {loading ? skeleton : thread && (
          <Link
            href={`/forum/${thread.id}`}
            className="flex flex-col gap-3 p-5 transition-transform duration-150 hover:-translate-y-0.5"
            style={{ background: 'var(--inverse-bg)', color: 'var(--inverse-text)', borderRadius: 18, boxShadow: '0 16px 40px rgba(28,43,38,0.2)' }}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="text-[11px] font-extrabold uppercase" style={{ color: 'var(--inverse-muted)', letterSpacing: '1.2px' }}>{t('Newest forum topic')}</div>
              <span className="text-[12px] font-semibold" style={{ color: 'var(--inverse-muted)' }}>{timeAgoShort(thread.createdAt)}</span>
            </div>
            {thread.ForumCategory && (
              <div className="text-[12.5px] font-bold" style={{ color: '#7fd4ab' }}>
                {thread.ForumCategory.emoji ? `${thread.ForumCategory.emoji} ` : ''}{thread.ForumCategory.name}
              </div>
            )}
            <div className="font-heading font-bold text-[18px] leading-snug" style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {localizedTitle(thread, lang)}
            </div>
            <div className="flex items-center gap-2.5 text-[12.5px] font-semibold mt-auto" style={{ color: 'var(--inverse-muted)' }}>
              <span style={{ color: '#5ec98f' }}>▲ {thread.score}</span>
              <span>·</span>
              <span className="inline-flex items-center gap-1"><MessageSquare size={12} />{thread.commentCount}</span>
              {thread.User?.username && (<><span>·</span><span>@{thread.User.username}</span></>)}
            </div>
          </Link>
        )}
      </div>
    </section>
  );
}

function AuthorRow({ trade }: { trade: TradeCardData }) {
  const a = trade.author;
  return (
    <div className="flex items-center gap-2.5 mt-auto pt-1">
      {a.avatarUrl ? (
        <img src={a.avatarUrl} alt={a.username} className="w-8 h-8 rounded-full object-cover" />
      ) : (
        <div className="w-8 h-8 rounded-full flex items-center justify-center font-extrabold text-[13px]" style={{ background: 'var(--accent)', color: 'var(--bg-base)' }}>
          {a.initial}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="text-[13.5px] font-bold truncate" style={{ color: 'var(--text-primary)' }}>{a.displayName || a.username}</div>
        <div className="text-[12px] truncate" style={{ color: 'var(--text-muted)' }}>
          @{a.username} · {timeAgoShort(trade.createdAt)}
          {a.winRate != null && a.totalTrades != null && a.totalTrades >= 3 && (
            <span style={{ color: 'var(--win)', fontWeight: 700 }}> · {a.winRate}% WR</span>
          )}
        </div>
      </div>
    </div>
  );
}
